import { Dropdown, Input, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import React, { useContext, useEffect, useState } from 'react'

import useDriverQueries from '../../../../hooks/useDriverQueries'
import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { capitaliseLastNames, capitaliseNames } from '../../../../utils/formatName'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { YourTheir } from '../../utils/driverCopyChange'
import { handleDropdownState } from '../../utils/handleDropdownState'
import { handlePrefilledDropdown } from '../../utils/handlePrefilledDropdown'
import { DriverNameStyle } from './DriverNameStyle'
const { h4: H4 } = typographyRem

export const DriverName = () => {
  const quote = useQuote()
  const {
    state: { selectedDriverIndex, isMobile, showModal },
  } = useContext(ProviderContext)
  const { updatedDriverDetails, setUpdatedDriverDetails, updatedAdditionalDriver, setUpdatedAdditionalDriver } =
    useDriverPageContext()
  const { titlesData } = useDriverQueries()
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()
  const additionalDrivers = quote?.details?.additional_drivers || []
  const selectedDriver = !selectedDriverIndex ? quote?.details?.proposer : additionalDrivers[selectedDriverIndex - 1]

  const [title, setTitle] = useState<string>('')
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')

  useEffect(() => {
    setTitle(selectedDriver?.title || '')
    setFirstName(selectedDriver?.first_names || '')
    setLastName(selectedDriver?.last_names || '')
    updateValidInputs({ field: 'title', value: !!selectedDriver?.title, index: selectedDriverIndex })
    updateValidInputs({ field: 'firstName', value: !!selectedDriver?.first_names, index: selectedDriverIndex })
    updateValidInputs({ field: 'lastName', value: !!selectedDriver?.last_names, index: selectedDriverIndex })
  }, [selectedDriverIndex])

  const titleError = showErrorBanner && !validInputs[selectedDriverIndex]?.title ? notAnsweredMessage : ''
  const firstNameError = showErrorBanner && !validInputs[selectedDriverIndex]?.firstName ? notAnsweredMessage : ''
  const lastNameError = showErrorBanner && !validInputs[selectedDriverIndex]?.lastName ? notAnsweredMessage : ''

  useEffect(() => {
    updateValidInputs({ field: 'title', value: !!title, index: selectedDriverIndex })
    if (title && title !== selectedDriver?.title) {
      DataLayer.customEvent({
        eventName: 'title',
        section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
        eventType: 'form tracking',
      })
      !selectedDriverIndex
        ? setUpdatedDriverDetails({ ...updatedDriverDetails, title })
        : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, title })
    }
  }, [title])

  useEffect(() => {
    updateValidInputs({ field: 'firstName', value: !!firstName, index: selectedDriverIndex })
  }, [firstName])

  useEffect(() => {
    updateValidInputs({ field: 'lastName', value: !!lastName, index: selectedDriverIndex })
  }, [lastName])

  const handleFirstNameBlur = () => {
    if (firstName && !firstNameError) {
      const formatedName = capitaliseNames(firstName)
      DataLayer.customEvent({
        eventName: 'first name',
        section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
        eventType: 'form tracking',
      })
      !selectedDriverIndex
        ? setUpdatedDriverDetails({ ...updatedDriverDetails, firstNames: formatedName })
        : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, first_names: formatedName })
    }
  }

  const handleLastNameBlur = () => {
    if (lastName && !lastNameError) {
      const formatedLastName = capitaliseLastNames(lastName)
      DataLayer.customEvent({
        eventName: 'last name',
        section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
        eventType: 'form tracking',
      })
      !selectedDriverIndex
        ? setUpdatedDriverDetails({ ...updatedDriverDetails, lastNames: formatedLastName })
        : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, last_names: formatedLastName })
    }
  }

  return (
    <DriverNameStyle>
      <GenericSectionStyle>
        <H4 className="title">{`${YourTheir()} name.`}</H4>
        <div className="generic-wrapper">
          <div id="title" />
          <div className="input-container">
            <Dropdown
              handleDropdownChange={option => setTitle(option.value)}
              isMobile={isMobile}
              placeholder="Select"
              options={titlesData?.titles || []}
              className="dropdown-above"
              errorMessage={titleError}
              states={handleDropdownState(title, titleError)}
              dataCy="title-dropdown-list"
              preFilled={handlePrefilledDropdown(titlesData?.titles || [], title)}
              tabFocusTrap={showModal ? -1 : 0}
              ariaLiveOnFocus
            />
          </div>
          <div id="firstName" />
          <div className="input-container">
            <Input
              placeHolder="First Name"
              name="first-name"
              value={firstName}
              onChange={value => setFirstName(value)}
              error={firstNameError}
              onBlur={handleFirstNameBlur}
              valid={!!firstName}
              dataCy="first-name-text-field"
              autoComplete="given-name"
              tabFocusTrap={showModal ? -1 : 0}
              ariaLiveOnFocus
            />
          </div>
          <div id="lastName" />
          <Input
            placeHolder="Last Name"
            name="last-name"
            value={lastName}
            onChange={value => setLastName(value)}
            error={lastNameError}
            onBlur={handleLastNameBlur}
            valid={!!lastName}
            dataCy="last-name-text-field"
            autoComplete="family-name"
            tabFocusTrap={showModal ? -1 : 0}
            ariaLiveOnFocus
          />
        </div>
      </GenericSectionStyle>
    </DriverNameStyle>
  )
}
